import { arrayMove } from '@dnd-kit/sortable';
import { Element, ElementContent } from 'hast';
import {
  ChartRow,
  ChartSeries,
  ParsedTable,
  SortedTableRow,
  SortDirection,
  TableSort,
  VariantLineChartData,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';

export function parseMarkdownTable(node: Element): ParsedTable {
  const head = findChildElement(node, `thead`);
  const body = findChildElement(node, `tbody`);
  const headerRow = head ? findChildElements(head, `tr`)[0] : undefined;
  const headers = headerRow
    ? findChildElements(headerRow, `th`).map(extractText)
    : [];
  const rows = body
    ? findChildElements(body, `tr`).map((row) =>
        findChildElements(row, `td`).map(extractText),
      )
    : [];

  return { headers, rows };
}

export function parseNumericValue(value: string) {
  const trimmedValue = value.trim();
  const numericPattern =
    /^\(?[+-]?(?:[$€£¥₹]\s*)?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?%?\)?$/;

  if (!numericPattern.test(trimmedValue)) {
    return null;
  }

  const isNegative = trimmedValue.startsWith(`(`);
  const valueWithoutFormatting = trimmedValue.replace(/[^\d.+-]/g, ``);
  const parsedValue = Number(valueWithoutFormatting);

  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return isNegative ? -parsedValue : parsedValue;
}

export function sortTableRows(
  rows: string[][],
  columnIndex?: number,
  direction?: SortDirection,
): SortedTableRow[] {
  const rowsWithOriginalIndexes = rows.map((values, originalIndex) => ({
    originalIndex,
    values,
  }));

  if (columnIndex === undefined || !direction) {
    return rowsWithOriginalIndexes;
  }

  return rowsWithOriginalIndexes.sort((firstRow, secondRow) => {
    const result = compareTableValues(
      firstRow.values[columnIndex] ?? ``,
      secondRow.values[columnIndex] ?? ``,
      direction,
    );

    return result || firstRow.originalIndex - secondRow.originalIndex;
  });
}

export function reorderColumns(
  columnOrder: number[],
  activeColumnIndex: number,
  overColumnIndex: number,
) {
  const oldIndex = columnOrder.indexOf(activeColumnIndex);
  const newIndex = columnOrder.indexOf(overColumnIndex);

  if (oldIndex < 0 || newIndex < 0) {
    return columnOrder;
  }

  return arrayMove(columnOrder, oldIndex, newIndex);
}

function compareTableValues(
  firstValue: string,
  secondValue: string,
  direction: SortDirection,
) {
  const firstIsMissing = isMissingTableValue(firstValue);
  const secondIsMissing = isMissingTableValue(secondValue);

  if (firstIsMissing || secondIsMissing) {
    if (firstIsMissing && secondIsMissing) {
      return 0;
    }

    return firstIsMissing ? 1 : -1;
  }

  const firstNumber = parseNumericValue(firstValue);
  const secondNumber = parseNumericValue(secondValue);
  const comparison =
    firstNumber !== null && secondNumber !== null
      ? firstNumber - secondNumber
      : firstValue.localeCompare(secondValue, undefined, {
          numeric: true,
          sensitivity: `base`,
        });

  return direction === `ascending` ? comparison : -comparison;
}

export function getNextSort(
  currentSort: TableSort | null,
  columnIndex: number,
): TableSort | null {
  if (currentSort?.columnIndex !== columnIndex) {
    return { columnIndex, direction: `ascending` };
  }

  if (currentSort.direction === `ascending`) {
    return { columnIndex, direction: `descending` };
  }

  return null;
}

export function getCurrentColumnOrder(
  columnOrder: number[],
  columnCount: number,
) {
  const availableColumnIndexes = Array.from(
    { length: columnCount },
    (_value, index) => index,
  );
  const currentColumnIndexes = columnOrder.filter((index) =>
    availableColumnIndexes.includes(index),
  );
  const newColumnIndexes = availableColumnIndexes.filter(
    (index) => !currentColumnIndexes.includes(index),
  );

  return [...currentColumnIndexes, ...newColumnIndexes];
}

export function findVariantColumnIndex(headers: string[]) {
  return headers.findIndex(
    (header) => header.trim().toLowerCase() === `variant`,
  );
}

export function findDefaultXAxisColumnIndex(
  columnCount: number,
  numericColumnIndexes: number[],
  variantColumnIndex: number,
) {
  const columnIndexes = Array.from(
    { length: columnCount },
    (_value, index) => index,
  ).filter((index) => index !== variantColumnIndex);

  return (
    columnIndexes.find((index) => !numericColumnIndexes.includes(index)) ??
    columnIndexes[0] ??
    0
  );
}

export function findNumericColumnIndexes(table: ParsedTable) {
  return table.headers.reduce<number[]>((indexes, _header, columnIndex) => {
    const values = table.rows
      .map((row) => row[columnIndex]?.trim())
      .filter(Boolean);
    const isNumericColumn =
      values.length > 0 &&
      values.some((value) => parseNumericValue(value) !== null) &&
      values.every(
        (value) =>
          parseNumericValue(value) !== null || isMissingTableValue(value),
      );

    return isNumericColumn ? [...indexes, columnIndex] : indexes;
  }, []);
}

function isMissingTableValue(value: string) {
  const normalizedValue = value.trim().toLowerCase();

  return (
    !normalizedValue || [`-`, `—`, `n/a`, `null`].includes(normalizedValue)
  );
}

export function createChartData(
  rows: string[][],
  xColumnIndex: number,
  yColumnIndex: number,
): ChartRow[] {
  return rows.reduce<ChartRow[]>((data, row) => {
    const formattedY = row[yColumnIndex] ?? ``;
    const y = parseNumericValue(formattedY);

    if (y === null) {
      return data;
    }

    return [...data, { formattedY, x: row[xColumnIndex] ?? ``, y }];
  }, []);
}

export function sortChartRowsByDate<T extends { x: string }>(rows: T[]): T[] {
  const rowsWithDates = rows.map((row, index) => ({
    date: parseChartDate(row.x),
    index,
    row,
  }));
  const populatedRows = rowsWithDates.filter(({ row }) => row.x.trim());
  const hasDateXAxis =
    populatedRows.length > 0 &&
    populatedRows.every(({ date }) => date !== null);

  if (!hasDateXAxis) {
    return rows;
  }

  return rowsWithDates
    .sort((firstRow, secondRow) => {
      if (firstRow.date === null || secondRow.date === null) {
        if (firstRow.date === null && secondRow.date === null) {
          return firstRow.index - secondRow.index;
        }

        return firstRow.date === null ? 1 : -1;
      }

      return firstRow.date - secondRow.date || firstRow.index - secondRow.index;
    })
    .map(({ row }) => row);
}

function parseChartDate(value: string) {
  const normalizedValue = value.trim().replace(/\s*\([^)]*\)\s*$/, ``);

  if (!isChartDateFormat(normalizedValue)) {
    return null;
  }

  const parsedDate = Date.parse(normalizedValue);

  return Number.isFinite(parsedDate) ? parsedDate : null;
}

function isChartDateFormat(value: string) {
  const monthName =
    `(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|` +
    `jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)`;

  return [
    /^\d{4}[-/.]\d{1,2}(?:[-/.]\d{1,2})?(?:$|[T\s])/,
    /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}(?:$|\s)/,
    new RegExp(`^${monthName}\\s+\\d{1,2}(?:,)?\\s+\\d{4}(?:$|\\s)`, `i`),
    new RegExp(`^\\d{1,2}\\s+${monthName}\\s+\\d{4}(?:$|\\s)`, `i`),
  ].some((pattern) => pattern.test(value));
}

export function createVariantChartSeries(
  rows: string[][],
  variantColumnIndex: number,
  xColumnIndex: number,
  yColumnIndex: number,
) {
  const seriesByVariant = new Map<string, ChartSeries>();

  rows.forEach((row) => {
    const variantName = row[variantColumnIndex]?.trim();

    if (!variantName) {
      return;
    }

    const variantKey = variantName.toLowerCase();
    const existingSeries = seriesByVariant.get(variantKey) ?? {
      data: [],
      name: variantName,
    };
    const formattedY = row[yColumnIndex] ?? ``;
    const y = parseNumericValue(formattedY);

    if (y !== null) {
      existingSeries.data.push({
        formattedY,
        x: row[xColumnIndex] ?? ``,
        y,
      });
    }

    seriesByVariant.set(variantKey, existingSeries);
  });

  return Array.from(seriesByVariant.values());
}

export function createVariantLineChartData(
  chartSeries: ChartSeries[],
): VariantLineChartData {
  const alignedSeries = alignVariantSeriesXValues(chartSeries);
  const series = alignedSeries.map((item, index) => ({
    dataKey: `variant_${index}`,
    name: item.name,
  }));
  const rowsByXValue = new Map<
    string,
    { x: string } & Record<string, number | string>
  >();

  alignedSeries.forEach((item, seriesIndex) => {
    item.data.forEach((point) => {
      const x = point.x.trim();

      if (!x) {
        return;
      }

      const row = rowsByXValue.get(x) ?? { x };
      row[series[seriesIndex].dataKey] = point.y;
      rowsByXValue.set(x, row);
    });
  });

  return { rows: Array.from(rowsByXValue.values()), series };
}

function alignVariantSeriesXValues(chartSeries: ChartSeries[]) {
  const referenceSeries = chartSeries.reduce<ChartSeries | undefined>(
    (currentReference, item) => {
      if (!currentReference) {
        return item;
      }

      return countDistinctXValues(item) > countDistinctXValues(currentReference)
        ? item
        : currentReference;
    },
    undefined,
  );
  const referenceXValues = referenceSeries?.data.map((point) => point.x) ?? [];
  const referenceDistinctXCount = referenceSeries
    ? countDistinctXValues(referenceSeries)
    : 0;

  return chartSeries.map((item) => {
    const shouldUseReferenceXValues =
      item !== referenceSeries &&
      referenceDistinctXCount > 1 &&
      countDistinctXValues(item) <= 1 &&
      referenceXValues.length >= item.data.length;

    if (!shouldUseReferenceXValues) {
      return item;
    }

    return {
      ...item,
      data: item.data.map((point, index) => ({
        ...point,
        x: referenceXValues[index],
      })),
    };
  });
}

function countDistinctXValues(chartSeries: ChartSeries) {
  return new Set(
    chartSeries.data.map((point) => point.x.trim()).filter(Boolean),
  ).size;
}

function findChildElement(node: Element, tagName: string) {
  return findChildElements(node, tagName)[0];
}

function findChildElements(node: Element, tagName: string) {
  return node.children.filter(
    (child): child is Element =>
      child.type === `element` && child.tagName === tagName,
  );
}

function extractText(node: ElementContent): string {
  if (node.type === `text`) {
    return node.value;
  }

  if (node.type !== `element`) {
    return ``;
  }

  return node.children.map(extractText).join(``).replace(/\s+/g, ` `).trim();
}
